import { Input } from '@react-apps/design-system';
import type {
  GuaranteeRule,
  GuaranteeRuleErrors,
  GuaranteeRuleField,
} from '../../domain/entities/guarantee.entity';

/** A régua como o formulário a segura: texto, para o campo poder ficar vazio. */
export type GuaranteeRuleDraft = Record<GuaranteeRuleField, string>;

export const DEFAULT_RULE_DRAFT: GuaranteeRuleDraft = {
  durationWeeks: '',
  weeklyTarget: '',
  dailyCap: '',
  minDaysPerWeek: '',
  eligibilityRatio: '',
};

export function ruleToDraft(rule: GuaranteeRule): GuaranteeRuleDraft {
  return {
    durationWeeks: String(rule.durationWeeks),
    weeklyTarget: String(rule.weeklyTarget),
    dailyCap: String(rule.dailyCap),
    minDaysPerWeek: String(rule.minDaysPerWeek),
    eligibilityRatio: String(rule.eligibilityRatio).replace('.', ','),
  };
}

/** Campo vazio vira `NaN`, que a validação recusa com a mensagem do campo. */
export function draftToRule(draft: GuaranteeRuleDraft): GuaranteeRule {
  const num = (v: string) =>
    v.trim() === '' ? Number.NaN : Number(v.trim().replace(',', '.'));
  return {
    durationWeeks: num(draft.durationWeeks),
    weeklyTarget: num(draft.weeklyTarget),
    dailyCap: num(draft.dailyCap),
    minDaysPerWeek: num(draft.minDaysPerWeek),
    eligibilityRatio: num(draft.eligibilityRatio),
  };
}

export const RULE_FIELDS: {
  field: GuaranteeRuleField;
  label: string;
  helperText: string;
}[] = [
  {
    field: 'durationWeeks',
    label: 'Duração (semanas)',
    helperText: 'Quantas semanas a garantia dura a partir da compra.',
  },
  {
    field: 'weeklyTarget',
    label: 'Meta semanal de questões',
    helperText: 'Questões que o aluno precisa resolver para a semana contar.',
  },
  {
    field: 'dailyCap',
    label: 'Teto diário de questões',
    helperText:
      'O máximo que um dia soma na meta. O que passar disso não conta.',
  },
  {
    field: 'minDaysPerWeek',
    label: 'Mínimo de dias por semana',
    helperText: 'Dias com questões, na mesma semana, para ela contar (1 a 7).',
  },
  {
    field: 'eligibilityRatio',
    label: 'Proporção de semanas cumpridas',
    helperText:
      'Fração das semanas que precisam fechar a meta para o aluno ter direito à garantia. 1 = todas; 0,8 = 80%.',
  },
];

interface GuaranteeRuleFieldsProps {
  value: GuaranteeRuleDraft;
  onChange: (next: GuaranteeRuleDraft) => void;
  errors: GuaranteeRuleErrors;
  /**
   * Gravação em voo. `readOnly`, não `disabled`: o campo desabilitado perde o
   * foco, e quem corrige pelo teclado depois do erro teria de voltar a ele.
   */
  readOnly?: boolean;
  idPrefix: string;
}

export function GuaranteeRuleFields({
  value,
  onChange,
  errors,
  readOnly,
  idPrefix,
}: GuaranteeRuleFieldsProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {RULE_FIELDS.map(({ field, label, helperText }) => (
        <Input
          key={field}
          id={`${idPrefix}-${field}`}
          label={label}
          helperText={helperText}
          error={errors[field]}
          inputMode={field === 'eligibilityRatio' ? 'decimal' : 'numeric'}
          value={value[field]}
          readOnly={readOnly}
          onChange={(e) => onChange({ ...value, [field]: e.target.value })}
        />
      ))}
    </div>
  );
}
